import { fmt, num } from './format.js';

/*
 * The Card page's activity feed (CLAUDE.md §4 "Card", Saif 21 Oct 2026): rows from GET /api/me/activity, grouped by
 * London day and worded for the list. Pure, so it's tested.
 */

export type ActivityType =
  | 'order_points' | 'spin' | 'credit_spent' | 'topup' | 'topup_bonus' | 'welcome' | 'email_bonus' | 'reward_claimed'
  | 'refund_points' | 'refund_credit' | 'game' | 'referral' | 'receipt_claim' | 'adjustment';

export interface ActivityRow { id: string; at: string; type: ActivityType; title: string; sub: string | null; points?: number; pence?: number; pending?: true; orderId?: string; topupId?: string }
export type ActivityFilter = 'all' | 'points' | 'credit';

const LONDON = 'Europe/London';
const dayKey = (d: Date): string => d.toLocaleDateString('en-CA', { timeZone: LONDON });

/** "Today", "Yesterday", else "Mon 19 Oct" (with the year when it isn't this one). */
export function dayLabel(at: string, now = new Date()): string {
  const d = new Date(at);
  const key = dayKey(d);
  if (key === dayKey(now)) return 'Today';
  if (key === dayKey(new Date(now.getTime() - 86400_000))) return 'Yesterday';
  const sameYear = d.toLocaleDateString('en-GB', { timeZone: LONDON, year: 'numeric' }) === now.toLocaleDateString('en-GB', { timeZone: LONDON, year: 'numeric' });
  const day = d.toLocaleDateString('en-GB', { timeZone: LONDON, weekday: 'short', day: 'numeric', month: 'short' });
  return sameYear ? day : `${day} ${d.toLocaleDateString('en-GB', { timeZone: LONDON, year: 'numeric' })}`;
}

/** The rows in their order, cut into days (newest first, as the feed comes). */
export function groupByDay(rows: readonly ActivityRow[], now = new Date()): { label: string; key: string; rows: ActivityRow[] }[] {
  const out: { label: string; key: string; rows: ActivityRow[] }[] = [];
  for (const r of rows) {
    const key = dayKey(new Date(r.at));
    const last = out[out.length - 1];
    if (last && last.key === key) last.rows.push(r);
    else out.push({ key, label: dayLabel(r.at, now), rows: [r] });
  }
  return out;
}

/** The round icon a row gets, by what it is. */
export function rowIcon(type: ActivityType): 'order' | 'topup' | 'bonus' | 'reward' | 'refund' | 'other' {
  switch (type) {
    case 'order_points': case 'credit_spent': case 'spin': return 'order';
    case 'topup': case 'topup_bonus': return 'topup';
    case 'welcome': case 'email_bonus': case 'game': case 'referral': case 'receipt_claim': return 'bonus';
    case 'reward_claimed': return 'reward';
    case 'refund_points': case 'refund_credit': return 'refund';
    default: return 'other';
  }
}

/** "+65 pts", "−£9.95", "+£5.00" (a pending row's subtitle says the points are on the way). */
export function amountText(r: Pick<ActivityRow, 'points' | 'pence' | 'pending'>): string {
  if (typeof r.points === 'number') return `${r.points < 0 ? '−' : '+'}${num(Math.abs(r.points))} pts`;
  if (typeof r.pence === 'number') return `${r.pence < 0 ? '−' : '+'}${fmt(Math.abs(r.pence))}`;
  return '';
}

/** Rows about points, rows about credit (a row is one or the other). */
export const isPointsRow = (r: Pick<ActivityRow, 'points'>): boolean => typeof r.points === 'number';

/** "14:32", for the row's second line in the full list. */
export const timeText = (at: string): string => new Date(at).toLocaleTimeString('en-GB', { timeZone: LONDON, hour: '2-digit', minute: '2-digit' });

/**
 * The Loyalty page's Activity tile: a few rows, with the points on the way (pending) capped so they can never crowd out
 * what has actually happened (Saif, 21 Oct: six uncollected test orders hid a top-up). The feed puts pending rows first.
 */
export function tileRows(rows: readonly ActivityRow[], caps: { pending: number; total: number } = { pending: 2, total: 5 }): ActivityRow[] {
  const pending = rows.filter((r) => r.pending).slice(0, caps.pending);
  const settled = rows.filter((r) => !r.pending);
  return [...pending, ...settled].slice(0, caps.total);
}
